/* Hoppo statiegeld — houdt de statiegeldregel in de winkelwagen gelijk aan het
   aantal bestelde blikjes.

   Liquid (snippets/hoppo-deposit.liquid) rekent bij elke render van de cart uit
   hoeveel statiegeld-eenheden er horen te zijn (data-expected) en hoeveel er in
   de cart staan (data-current). Verschillen die, dan zet dit script de
   statiegeldregel via /cart/update.js op het juiste aantal en dispatcht het
   dezelfde CartLinesUpdateEvent als Horizon zelf, zodat cart-drawer, cartpagina
   en header-bubble opnieuw renderen. Die nieuwe render levert weer een
   <hoppo-deposit-sync> met bijgewerkte attributen op; zijn expected en current
   dan gelijk, dan gebeurt er niets meer.

   Zolang een sync loopt is de afrekenknop uitgeschakeld en wordt een submit van
   het cart-formulier uitgesteld tot de sync klaar is, zodat de checkout nooit
   zonder (of met te veel) statiegeld start. */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const MAX_ATTEMPTS_PER_TARGET = 2;

/** @type {Promise<void> | null} */
let pending = null;
/** @type {string} */
let lastTarget = '';
let attempts = 0;

class HoppoDepositSync extends HTMLElement {
  static observedAttributes = ['data-expected', 'data-current', 'data-variant-id'];

  connectedCallback() {
    this.#schedule();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  #schedule() {
    // Morph zet attributen één voor één; wacht tot ze allemaal staan.
    queueMicrotask(() => {
      if (this.isConnected) sync(readState(this));
    });
  }
}

/**
 * @typedef {{ variantId: string, expected: number, current: number }} DepositState
 */

/**
 * @param {Element | null | undefined} el
 * @returns {DepositState | null}
 */
function readState(el) {
  if (!(el instanceof HTMLElement)) return null;

  const variantId = el.dataset.variantId ?? '';
  const expected = Number(el.dataset.expected);
  const current = Number(el.dataset.current);

  if (!variantId || !Number.isFinite(expected) || !Number.isFinite(current)) return null;

  return { variantId, expected, current };
}

/**
 * Tweede trigger naast het custom element: leest de aantallen uit de
 * cart-secties die Horizon bij elke cart-wijziging meestuurt. Dekt gevallen
 * waarin de morph het element niet (opnieuw) aanraakt.
 * @param {Record<string, string> | undefined} sections
 * @returns {DepositState | null}
 */
function readStateFromSections(sections) {
  if (!sections) return null;

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('hoppo-deposit-sync')) continue;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const state = readState(doc.querySelector('hoppo-deposit-sync'));
    if (state) return state;
  }

  return null;
}

/** @param {DepositState | null} state */
function sync(state) {
  if (!state || pending) return;

  const { variantId, expected, current } = state;

  if (expected === current) {
    lastTarget = '';
    attempts = 0;
    setCheckoutBusy(false);
    return;
  }

  // Loop-beveiliging: convergeert dezelfde doelstand niet (bijv. voorraadlimiet
  // op het statiegeld-product), dan stoppen we in plaats van eindeloos te posten.
  const target = `${variantId}:${expected}`;
  if (target === lastTarget && attempts >= MAX_ATTEMPTS_PER_TARGET) {
    console.warn('[hoppo-deposit] statiegeldregel kon niet op', expected, 'worden gezet');
    setCheckoutBusy(false);
    return;
  }
  if (target !== lastTarget) {
    lastTarget = target;
    attempts = 0;
  }
  attempts += 1;

  pending = update(variantId, expected).finally(() => {
    pending = null;
  });
}

/**
 * @param {string} variantId
 * @param {number} quantity
 */
async function update(variantId, quantity) {
  setCheckoutBusy(true);

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  const body = JSON.stringify({
    updates: { [variantId]: quantity },
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });

  const deferred = CartLinesUpdateEvent.createPromise();

  // Op document, niet op het element: dat kan tijdens de fetch door een morph
  // uit de DOM verdwijnen en dan bubbelt het event niet meer.
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const cart = await response.json();

    if (!response.ok || cart.status || cart.errors) {
      throw new Error(cart.description || cart.message || cart.errors || 'Statiegeld bijwerken mislukt');
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: cart.sections,
        itemCount: cart.item_count,
        source: 'hoppo-deposit',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    document.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Statiegeld bijwerken mislukt',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    // De cart is server-side al bijgewerkt, dus afrekenen kan weer. De render
    // die hierop volgt zet de knop opnieuw op busy als er nog een verschil is.
    setCheckoutBusy(false);
  }
}

/** @param {boolean} busy */
function setCheckoutBusy(busy) {
  for (const button of document.querySelectorAll('.cart__checkout-button')) {
    if (!(button instanceof HTMLButtonElement)) continue;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else if (button.getAttribute('aria-busy') === 'true') {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }
}

// Vangnet: een submit van het cart-formulier tijdens een sync wachten we af en
// herhalen we daarna met dezelfde knop (name="checkout" bepaalt de bestemming).
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!pending || !(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

    event.preventDefault();
    const submitterName = event.submitter instanceof HTMLButtonElement ? event.submitter.name : '';

    pending.then(() => {
      const submitter = submitterName ? document.querySelector(`button[name="${submitterName}"][form="cart-form"]`) : null;
      form.requestSubmit(submitter instanceof HTMLButtonElement ? submitter : undefined);
    });
  },
  true
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  /** @type {any} */ (event).promise
    ?.then((/** @type {any} */ result) => {
      // De meegestuurde secties zijn de serverstand ná de wijziging; de DOM kan
      // op dit moment nog midden in de morph zitten en is daarom het vangnet.
      requestAnimationFrame(() => {
        const state = readStateFromSections(result?.detail?.sections) ?? readState(document.querySelector('hoppo-deposit-sync'));
        sync(state);
      });
    })
    .catch(() => {});
});

if (!customElements.get('hoppo-deposit-sync')) {
  customElements.define('hoppo-deposit-sync', HoppoDepositSync);
}
